import { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { SceneManager } from '@/engine/SceneManager.js'
import { CardMesh } from '@/engine/CardMesh.js'
import { CameraControls } from '@/engine/CameraControls.js'
import { AnimationLoop } from '@/engine/AnimationLoop.js'
import { TemplateManager } from '@/templates/TemplateManager.js'
import { TemplateRenderer } from '@/templates/TemplateRenderer.js'

const templateManager = new TemplateManager()
const templateRenderer = new TemplateRenderer()

export function CardCanvas({ cardData }) {
  const containerRef = useRef(null)
  const sceneRef = useRef(null)
  const cardMeshRef = useRef(null)
  const controlsRef = useRef(null)
  const animationLoopRef = useRef(null)
  const [templateLoaded, setTemplateLoaded] = useState(false)

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return

    console.log('Initializing Three.js scene...')
    const sceneManager = new SceneManager(containerRef.current)
    const cardMesh = new CardMesh()

    // Add card to scene
    sceneManager.add(cardMesh.getMesh())

    // Set up camera controls
    const cameraControls = new CameraControls(sceneManager.camera, sceneManager.getDomElement())

    // Create placeholder texture for now
    const placeholderTexture = CardMesh.createPlaceholderTexture()
    cardMesh.setFrontTexture(placeholderTexture)
    cardMesh.setBackTexture(placeholderTexture)

    // Set up animation loop with sceneManager and controls
    const animationLoop = new AnimationLoop(sceneManager, cameraControls)
    animationLoop.start()

    // Store refs
    sceneRef.current = sceneManager
    cardMeshRef.current = cardMesh
    controlsRef.current = cameraControls
    animationLoopRef.current = animationLoop

    console.log('Three.js scene initialized successfully')

    // Load template
    loadTemplate()

    // Cleanup on unmount
    return () => {
      console.log('Cleaning up Three.js scene...')
      animationLoop.stop()
      cameraControls.dispose()
      cardMesh.dispose()
      sceneManager.dispose()
    }
  }, [])

  // Load card template
  async function loadTemplate() {
    try {
      console.log('Loading Slay the Spire template...')
      await templateManager.loadTemplate('slay-the-spire')
      await templateManager.setCurrentTemplate('slay-the-spire')
      setTemplateLoaded(true)
      console.log('Template loaded successfully')
    } catch (error) {
      console.error('Failed to load template:', error)
    }
  }

  // Update card texture when cardData changes
  useEffect(() => {
    if (!cardMeshRef.current || !templateLoaded) return

    updateCardTexture()
  }, [cardData, templateLoaded])

  // Update card texture based on current data
  function updateCardTexture() {
    try {
      const template = templateManager.getCurrentTemplate()
      const cardType = cardData.type || 'Attack'

      // Get template image for current card type
      const frontImage = templateManager.getTemplateImage('slay-the-spire', cardType, 'front')
      const backImage = templateManager.getTemplateImage('slay-the-spire', cardType, 'back')

      if (!frontImage || !template) {
        console.warn('Template not ready yet')
        return
      }

      // Map our cardData to template property names
      // The template uses different property IDs for front and back
      const templateData = {
        // Front side properties
        cardName: cardData.name,
        cardText: cardData.description,
        energyCost: cardData.energyCost,
        // Back side properties (same data, renderer will add "+" and green color)
        cardNameBack: cardData.name,
        cardTextBack: cardData.description,
        energyCostBack: cardData.energyCost,
        // Artwork - ensure it's in the format { front: img, back: img }
        artwork: cardData.artwork ? {
          front: cardData.artwork,
          back: cardData.artwork
        } : null,
        upgraded: false // Front is not upgraded, back will be set by renderer
      }

      // Render front side
      templateRenderer.render(frontImage, template, templateData, 'front')
      // Clone the canvas to prevent it from being overwritten
      const frontCanvas = document.createElement('canvas')
      frontCanvas.width = templateRenderer.canvas.width
      frontCanvas.height = templateRenderer.canvas.height
      frontCanvas.getContext('2d').drawImage(templateRenderer.canvas, 0, 0)
      const frontTexture = new THREE.CanvasTexture(frontCanvas)
      frontTexture.needsUpdate = true
      frontTexture.minFilter = THREE.LinearFilter
      frontTexture.magFilter = THREE.LinearFilter
      frontTexture.colorSpace = THREE.SRGBColorSpace // Fix washed out colors

      // Render back side
      templateRenderer.render(backImage, template, templateData, 'back')
      // Clone the canvas to prevent issues with shared references
      const backCanvas = document.createElement('canvas')
      backCanvas.width = templateRenderer.canvas.width
      backCanvas.height = templateRenderer.canvas.height
      backCanvas.getContext('2d').drawImage(templateRenderer.canvas, 0, 0)
      const backTexture = new THREE.CanvasTexture(backCanvas)
      backTexture.needsUpdate = true
      backTexture.minFilter = THREE.LinearFilter
      backTexture.magFilter = THREE.LinearFilter
      backTexture.colorSpace = THREE.SRGBColorSpace // Fix washed out colors

      // Update card mesh
      cardMeshRef.current.setFrontTexture(frontTexture)
      cardMeshRef.current.setBackTexture(backTexture)

      console.log('Card texture updated')
    } catch (error) {
      console.error('Error updating card texture:', error)
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[calc(100vh-12rem)] rounded-xl overflow-hidden bg-card glass"
    >
      {/* Controls hint */}
      <div className="absolute bottom-4 right-4 glass px-4 py-2 rounded-lg text-xs text-muted-foreground pointer-events-none">
        Drag to rotate • Scroll to zoom
      </div>
    </div>
  )
}
